import { ChevronRightIcon, Repeat } from 'lucide-react';

import { cadenceSentence } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type { RecurringCadence } from '@splitfiesta/shared';
import { Card } from '@splitfiesta/ui';

import { RouteChangeLink } from '@/components/common/RouteChangeLink';

import { useFeatureFlag } from '@/hooks/queries/useFeatureFlagsQuery';

interface ExpenseRecurringRowProps {
  groupId: string;
  cadence: RecurringCadence;
}

/** "Repeats every month" on an expense a rule made or started; opens the group's list. */
export function ExpenseRecurringRow({
  groupId,
  cadence,
}: ExpenseRecurringRowProps) {
  const { t } = useTranslation();
  // The list page exists only with the flag; without it the row is just text.
  const linked = useFeatureFlag('pro');
  const sentence = cadenceSentence(cadence);
  const content = (
    <>
      <Repeat
        aria-hidden="true"
        className="h-5 w-5 shrink-0 text-orange-500 dark:text-orange-400"
      />
      <span className="min-w-0 flex-1 break-words font-medium text-gray-900 dark:text-gray-100">
        {t('expenses:repeat.detail', {
          cadence: t(sentence.key, { count: sentence.count }),
        })}
      </span>
      {linked && (
        <ChevronRightIcon
          aria-hidden="true"
          className="ml-auto h-5 w-5 shrink-0 text-gray-400"
        />
      )}
    </>
  );
  const rowClasses = 'flex w-full min-h-11 items-center gap-3 rounded-md';

  return (
    <Card>
      {linked ? (
        <RouteChangeLink
          href={`/groups/${groupId}/recurring`}
          data-testid="expense-recurring-row"
          className={`${rowClasses} text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500`}
        >
          {content}
        </RouteChangeLink>
      ) : (
        <div data-testid="expense-recurring-row" className={rowClasses}>
          {content}
        </div>
      )}
    </Card>
  );
}
